import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  Check, 
  X, 
  Sparkles, 
  Edit2, 
  Save, 
  Eye, 
  Maximize2, 
  Minimize2,
  Trash2,
  CheckSquare,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  RefreshCw
} from "lucide-react";
import { useDropzone } from "react-dropzone";
import axios from "axios";

const InvoiceScanner = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [pdfExpanded, setPdfExpanded] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  
  // Multi-select state
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Filter state
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    vendor: "",
    invoice_number: "",
    total_amount: "",
    due_date: ""
  });

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Kunde inte hämta fakturor");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(f => f.type === "application/pdf");
    if (pdfFiles.length === 0) {
      toast.error("Endast PDF-filer stöds");
      return;
    }

    setUploading(true);
    for (const file of pdfFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        await axios.post(`${API}/invoices/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        toast.success(`${file.name} uppladdad`);
      } catch (error) {
        toast.error(`Fel vid uppladdning av ${file.name}`);
      }
    }
    setUploading(false);
    fetchInvoices();
  }, [token]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: true
  });

  const handleStatusChange = async (invoice, status) => {
    try {
      await axios.put(`${API}/invoices/${invoice.id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(status === "approved" ? "Faktura godkänd" : "Faktura avvisad");
      fetchInvoices();
    } catch (error) {
      toast.error("Kunde inte uppdatera status");
    }
  };

  const handleEdit = (invoice) => {
    setEditingId(invoice.id);
    setEditData({
      vendor: invoice.vendor || "",
      invoice_number: invoice.invoice_number || "",
      invoice_date: invoice.invoice_date || "",
      due_date: invoice.due_date || "",
      total_amount: invoice.total_amount || "",
      vat_amount: invoice.vat_amount || "",
      net_amount: invoice.net_amount || ""
    });
  };

  const handleSaveEdit = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Train the model with corrections
      await axios.post(`${API}/invoices/${invoiceId}/train`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Faktura uppdaterad och AI tränad");
      setEditingId(null);
      fetchInvoices();
    } catch (error) {
      toast.error("Kunde inte spara ändringar");
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Faktura borttagen");
      fetchInvoices();
    } catch (error) {
      toast.error("Kunde inte ta bort faktura");
    }
  };

  // Bulk actions
  const handleBulkApprove = async () => {
    if (selectedInvoices.size === 0) return;
    
    let approvedCount = 0;
    let failedCount = 0;
    let skippedCount = 0;
    
    for (const invoiceId of selectedInvoices) {
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice || invoice.status !== "pending") {
        skippedCount++;
        continue;
      }
      
      try {
        await axios.put(`${API}/invoices/${invoiceId}`, { status: "approved" }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        approvedCount++;
      } catch (error) {
        failedCount++;
        console.error(`Failed to approve invoice ${invoiceId}:`, error);
      }
    }
    
    let message = `${approvedCount} godkända`;
    if (skippedCount > 0) message += `, ${skippedCount} överhoppade`;
    if (failedCount > 0) message += `, ${failedCount} misslyckades`;
    
    if (approvedCount > 0) {
      toast.success(message);
    } else if (skippedCount > 0) {
      toast.info(message);
    } else {
      toast.error("Kunde inte godkänna några fakturor");
    }
    
    setSelectedInvoices(new Set());
    fetchInvoices();
  };

  const handleBulkReject = async () => {
    if (selectedInvoices.size === 0) return;
    
    let rejectedCount = 0;
    let failedCount = 0;
    let skippedCount = 0;
    
    for (const invoiceId of selectedInvoices) {
      const invoice = invoices.find(i => i.id === invoiceId);
      if (!invoice || invoice.status !== "pending") {
        skippedCount++;
        continue;
      }
      
      try {
        await axios.put(`${API}/invoices/${invoiceId}`, { status: "rejected" }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        rejectedCount++;
      } catch (error) {
        failedCount++;
        console.error(`Failed to reject invoice ${invoiceId}:`, error);
      }
    }
    
    let message = `${rejectedCount} avvisade`;
    if (skippedCount > 0) message += `, ${skippedCount} överhoppade`;
    if (failedCount > 0) message += `, ${failedCount} misslyckades`;
    
    if (rejectedCount > 0) {
      toast.success(message);
    } else if (skippedCount > 0) {
      toast.info(message);
    } else {
      toast.error("Kunde inte avvisa några fakturor");
    }
    
    setSelectedInvoices(new Set());
    fetchInvoices();
  };

  const handleBulkDelete = async () => {
    if (selectedInvoices.size === 0) return;
    
    let deletedCount = 0;
    let failedCount = 0;
    
    for (const invoiceId of selectedInvoices) {
      try {
        await axios.delete(`${API}/invoices/${invoiceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deletedCount++;
      } catch (error) {
        failedCount++;
        console.error(`Failed to delete invoice ${invoiceId}:`, error);
      }
    }
    
    if (deletedCount > 0 && failedCount === 0) {
      toast.success(`${deletedCount} fakturor borttagna`);
    } else if (deletedCount > 0 && failedCount > 0) {
      toast.warning(`${deletedCount} borttagna, ${failedCount} misslyckades`);
    } else {
      toast.error("Kunde inte ta bort några fakturor");
    }
    
    setSelectedInvoices(new Set());
    fetchInvoices();
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="ml-1 text-slate-400" />;
    }
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1 text-indigo-600" />
      : <ArrowDown size={14} className="ml-1 text-indigo-600" />;
  };

  const updateColumnFilter = (column, value) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
  };

  // Filtered and sorted invoices
  const filteredInvoices = invoices
    .filter(inv => {
      const matchesStatus = filterStatus === "all" || inv.status === filterStatus;
      const matchesSearch = !searchTerm || 
        inv.vendor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.filename?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesVendor = !columnFilters.vendor || 
        inv.vendor?.toLowerCase().includes(columnFilters.vendor.toLowerCase());
      const matchesInvoiceNumber = !columnFilters.invoice_number || 
        inv.invoice_number?.toLowerCase().includes(columnFilters.invoice_number.toLowerCase());
      const matchesAmount = !columnFilters.total_amount || 
        String(inv.total_amount || "").includes(columnFilters.total_amount);
      const matchesDueDate = !columnFilters.due_date || 
        inv.due_date?.includes(columnFilters.due_date);
      
      return matchesStatus && matchesSearch && matchesVendor && 
             matchesInvoiceNumber && matchesAmount && matchesDueDate;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case "vendor":
          aVal = (a.vendor || "").toLowerCase();
          bVal = (b.vendor || "").toLowerCase();
          break;
        case "invoice_number":
          aVal = a.invoice_number || "";
          bVal = b.invoice_number || "";
          break;
        case "total_amount":
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case "due_date":
          aVal = a.due_date || "";
          bVal = b.due_date || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "created_at":
        default:
          aVal = a.created_at || "";
          bVal = b.created_at || "";
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  // Selection handlers
  const pendingInvoices = filteredInvoices.filter(i => i.status === "pending");
  
  const toggleSelectAll = () => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map(i => i.id)));
    }
  };

  const toggleInvoice = (id) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedInvoices(newSelected);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "-";
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 2
    }).format(amount);
  };

  const openPdfPreview = async (invoice) => {
    setPreviewInvoice(invoice);
    setPdfExpanded(false);
    setPdfError(false);
    
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          Range: 'bytes=0-0'
        },
        validateStatus: (status) => status < 500
      });
      if (response.status === 404) {
        setPdfError(true);
      }
    } catch (error) {
      setPdfError(true);
    }
  };

  const closePdfPreview = () => {
    setPreviewInvoice(null);
    setPdfExpanded(false);
    setPdfError(false);
  };

  const getPdfUrl = (invoiceId) => {
    return `${API}/invoices/${invoiceId}/pdf?token=${token}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-700">Godkänd</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700">Avvisad</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700">Väntar</Badge>;
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.8) return <Badge className="bg-green-100 text-green-700">Hög</Badge>;
    if (confidence >= 0.5) return <Badge className="bg-amber-100 text-amber-700">Medium</Badge>;
    return <Badge className="bg-red-100 text-red-700">Låg</Badge>;
  };

  // Count by status
  const pendingCount = invoices.filter(i => i.status === "pending").length;
  const approvedCount = invoices.filter(i => i.status === "approved").length;
  const rejectedCount = invoices.filter(i => i.status === "rejected").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoice-scanner">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fakturaskanner</h1>
          <p className="text-slate-500">Ladda upp PDF-fakturor och låt AI extrahera data</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <FileText className="text-indigo-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.length}</p>
                <p className="text-xs text-slate-500">Totalt fakturor</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Sparkles className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
                <p className="text-xs text-slate-500">Väntar på granskning</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Check className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
                <p className="text-xs text-slate-500">Godkända</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <X className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
                <p className="text-xs text-slate-500">Avvisade</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Zone */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div
            {...getRootProps()}
            className={`p-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              isDragActive ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-indigo-300"
            }`}
            data-testid="upload-dropzone"
          >
            <input {...getInputProps()} data-testid="file-input" />
            <div className="flex flex-col items-center">
              {uploading ? (
                <>
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                    <RefreshCw className="animate-spin text-indigo-600" size={24} />
                  </div>
                  <p className="font-medium text-slate-900">Analyserar fakturor...</p>
                  <p className="text-slate-500 text-sm">AI extraherar data från PDF</p>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                    <Upload className="text-indigo-600" size={24} />
                  </div>
                  <p className="font-medium text-slate-900">
                    {isDragActive ? "Släpp filerna här" : "Dra och släpp PDF-fakturor här"}
                  </p>
                  <p className="text-slate-500 text-sm">eller klicka för att välja filer</p>
                  <div className="mt-3 flex items-center gap-2 text-sm text-indigo-600">
                    <Sparkles size={14} />
                    <span>AI-driven extraktion av fakturadata</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedInvoices.size > 0 && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckSquare className="text-indigo-600" size={20} />
                <span className="font-medium text-indigo-900">
                  {selectedInvoices.size} fakturor valda
                </span>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedInvoices(new Set())}
                >
                  Avmarkera alla
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={handleBulkDelete}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 size={14} className="mr-2" />
                  Ta bort valda
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={handleBulkReject}
                  data-testid="bulk-reject-btn"
                >
                  <X size={14} className="mr-2" />
                  Avvisa valda
                </Button>
                <Button 
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleBulkApprove}
                  data-testid="bulk-approve-btn"
                >
                  <Check size={14} className="mr-2" />
                  Godkänn valda
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices Table */}
      <Card data-testid="invoices-list">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Uppladdade fakturor ({filteredInvoices.length})</CardTitle>
            <div className="flex gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input 
                  placeholder="Sök fakturor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla</SelectItem>
                  <SelectItem value="pending">Väntar</SelectItem>
                  <SelectItem value="approved">Godkända</SelectItem>
                  <SelectItem value="rejected">Avvisade</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedInvoices.size === filteredInvoices.length && filteredInvoices.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left p-3">
                      <button 
                        className="flex items-center hover:text-indigo-600 transition-colors font-medium"
                        onClick={() => handleSort("vendor")}
                      >
                        Leverantör
                        {getSortIcon("vendor")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button 
                        className="flex items-center hover:text-indigo-600 transition-colors font-medium"
                        onClick={() => handleSort("invoice_number")}
                      >
                        Fakturanr
                        {getSortIcon("invoice_number")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button 
                        className="flex items-center justify-end hover:text-indigo-600 transition-colors font-medium w-full"
                        onClick={() => handleSort("total_amount")}
                      >
                        Belopp
                        {getSortIcon("total_amount")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button 
                        className="flex items-center hover:text-indigo-600 transition-colors font-medium"
                        onClick={() => handleSort("due_date")}
                      >
                        Förfallodatum
                        {getSortIcon("due_date")}
                      </button>
                    </th>
                    <th className="text-center p-3">
                      <button 
                        className="flex items-center justify-center hover:text-indigo-600 transition-colors font-medium w-full"
                        onClick={() => handleSort("status")}
                      >
                        Status
                        {getSortIcon("status")}
                      </button>
                    </th>
                    <th className="text-center p-3 font-medium">Konfidens</th>
                    <th className="text-right p-3 font-medium">Åtgärder</th>
                  </tr>
                  {/* Filter row */}
                  <tr className="bg-slate-100/50">
                    <th className="p-1"></th>
                    <th className="p-1">
                      <Input
                        placeholder="Sök leverantör..."
                        value={columnFilters.vendor}
                        onChange={(e) => updateColumnFilter("vendor", e.target.value)}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1">
                      <Input
                        placeholder="Sök fakturanr..."
                        value={columnFilters.invoice_number}
                        onChange={(e) => updateColumnFilter("invoice_number", e.target.value)}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1">
                      <Input
                        placeholder="Sök belopp..."
                        value={columnFilters.total_amount}
                        onChange={(e) => updateColumnFilter("total_amount", e.target.value)}
                        className="h-7 text-xs text-right"
                      />
                    </th>
                    <th className="p-1">
                      <Input
                        placeholder="Sök datum..."
                        value={columnFilters.due_date}
                        onChange={(e) => updateColumnFilter("due_date", e.target.value)}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      className={`border-b border-slate-100 hover:bg-slate-50 ${
                        selectedInvoices.has(invoice.id) ? 'bg-indigo-50' : ''
                      }`}
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="p-3">
                        <Checkbox 
                          checked={selectedInvoices.has(invoice.id)}
                          onCheckedChange={() => toggleInvoice(invoice.id)}
                          data-testid={`invoice-checkbox-${invoice.id}`}
                        />
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{invoice.vendor || "-"}</p>
                          <p className="text-xs text-slate-500">{invoice.filename}</p>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs">{invoice.invoice_number || "-"}</td>
                      <td className="p-3 text-right font-mono font-medium">
                        {formatCurrency(invoice.total_amount)}
                      </td>
                      <td className="p-3 text-xs">{invoice.due_date || "-"}</td>
                      <td className="p-3 text-center">{getStatusBadge(invoice.status)}</td>
                      <td className="p-3 text-center">{getConfidenceBadge(invoice.confidence)}</td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openPdfPreview(invoice)}
                            title="Visa PDF"
                          >
                            <Eye size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(invoice)}
                            title="Redigera"
                          >
                            <Edit2 size={14} />
                          </Button>
                          {invoice.status === "pending" && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => handleStatusChange(invoice, "rejected")}
                                title="Avvisa"
                              >
                                <X size={14} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-green-500 hover:text-green-600"
                                onClick={() => handleStatusChange(invoice, "approved")}
                                title="Godkänn"
                              >
                                <Check size={14} />
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(invoice.id)}
                            title="Ta bort"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <FileText className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga fakturor {filterStatus !== "all" ? `med status "${filterStatus}"` : "uppladdade ännu"}</p>
              <p className="text-sm mt-1">Dra och släpp PDF-filer ovan för att komma igång</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {editingId && (
        <Dialog open={!!editingId} onOpenChange={() => setEditingId(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Redigera faktura</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Leverantör</Label>
                <Input
                  value={editData.vendor}
                  onChange={(e) => setEditData({ ...editData, vendor: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Fakturanummer</Label>
                <Input
                  value={editData.invoice_number}
                  onChange={(e) => setEditData({ ...editData, invoice_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Fakturadatum</Label>
                <Input
                  type="date"
                  value={editData.invoice_date}
                  onChange={(e) => setEditData({ ...editData, invoice_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Förfallodatum</Label>
                <Input
                  type="date"
                  value={editData.due_date}
                  onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Totalbelopp</Label>
                <Input
                  type="number"
                  value={editData.total_amount}
                  onChange={(e) => setEditData({ ...editData, total_amount: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Moms</Label>
                <Input
                  type="number"
                  value={editData.vat_amount}
                  onChange={(e) => setEditData({ ...editData, vat_amount: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nettobelopp</Label>
                <Input
                  type="number"
                  value={editData.net_amount}
                  onChange={(e) => setEditData({ ...editData, net_amount: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditingId(null)}>
                Avbryt
              </Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => handleSaveEdit(editingId)}>
                <Save size={14} className="mr-2" />
                Spara & Träna AI
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* PDF Preview Dialog */}
      <Dialog open={!!previewInvoice} onOpenChange={() => closePdfPreview()}>
        <DialogContent className={`${pdfExpanded ? 'max-w-[95vw] h-[95vh]' : 'max-w-5xl h-[80vh]'} flex flex-col p-0`} data-testid="pdf-preview-dialog">
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <FileText size={20} className="text-indigo-600" />
                {previewInvoice?.filename || "PDF-förhandsgranskning"}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPdfExpanded(!pdfExpanded)}
                  data-testid="toggle-expand-btn"
                >
                  {pdfExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex flex-1 overflow-hidden">
            {/* PDF Viewer */}
            <div className={`${pdfExpanded ? 'w-2/3' : 'w-1/2'} border-r bg-slate-100 overflow-hidden flex items-center justify-center`}>
              {previewInvoice && !pdfError ? (
                <object
                  data={getPdfUrl(previewInvoice.id)}
                  type="application/pdf"
                  className="w-full h-full"
                  data-testid="pdf-iframe"
                >
                  <div className="text-center p-8">
                    <FileText className="mx-auto mb-4 text-slate-300" size={64} />
                    <p className="text-slate-500 font-medium">PDF kan inte visas</p>
                    <a 
                      href={getPdfUrl(previewInvoice.id)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Öppna PDF i ny flik
                    </a>
                  </div>
                </object>
              ) : (
                <div className="text-center p-8">
                  <FileText className="mx-auto mb-4 text-slate-300" size={64} />
                  <p className="text-slate-500 font-medium">PDF ej tillgänglig</p>
                  <p className="text-slate-400 text-sm mt-2">
                    Originalfilen sparades inte för denna faktura.
                  </p>
                </div>
              )}
            </div>
            
            {/* Extracted Data Panel */}
            <div className={`${pdfExpanded ? 'w-1/3' : 'w-1/2'} overflow-y-auto p-6 bg-white`}>
              {previewInvoice && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="text-indigo-600" size={18} />
                    <h3 className="font-semibold text-slate-900">AI-extraherad data</h3>
                    {previewInvoice.confidence && (
                      <Badge className={previewInvoice.confidence >= 0.8 ? "bg-green-100 text-green-700" : previewInvoice.confidence >= 0.5 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>
                        {Math.round(previewInvoice.confidence * 100)}% konfidens
                      </Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Leverantör</Label>
                      <p className="font-medium text-slate-900">{previewInvoice.vendor || "-"}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Fakturanummer</Label>
                      <p className="font-medium font-mono text-slate-900">{previewInvoice.invoice_number || "-"}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Fakturadatum</Label>
                      <p className="font-medium text-slate-900">{previewInvoice.invoice_date || "-"}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Förfallodatum</Label>
                      <p className="font-medium text-slate-900">{previewInvoice.due_date || "-"}</p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Belopp</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Nettobelopp</span>
                        <span className="font-medium">{formatCurrency(previewInvoice.net_amount)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Moms</span>
                        <span className="font-medium">{formatCurrency(previewInvoice.vat_amount)}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="font-semibold text-slate-900">Totalt</span>
                        <span className="font-bold text-lg text-indigo-600">{formatCurrency(previewInvoice.total_amount)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <Label className="text-xs text-slate-500">Status</Label>
                    <div className="mt-1">{getStatusBadge(previewInvoice.status)}</div>
                  </div>

                  {previewInvoice.status === "pending" && (
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        className="flex-1 text-red-500 hover:text-red-600"
                        onClick={() => {
                          handleStatusChange(previewInvoice, "rejected");
                          closePdfPreview();
                        }}
                        data-testid="preview-reject-btn"
                      >
                        <X size={16} className="mr-2" />
                        Avvisa
                      </Button>
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          handleStatusChange(previewInvoice, "approved");
                          closePdfPreview();
                        }}
                        data-testid="preview-approve-btn"
                      >
                        <Check size={16} className="mr-2" />
                        Godkänn
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceScanner;
